import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api-client.js';
import { T, Badge, Card, StatCard, font } from '../theme.js';

// ============================================================
// RM DASHBOARD — "What Needs Attention Today"
// ============================================================

const CATEGORIES = [
  { key: null, label: 'All' },
  { key: 'ALERT', label: 'Alerts' },
  { key: 'KYC_REVIEW', label: 'KYC Reviews' },
  { key: 'COMPLAINT', label: 'Complaints' },
  { key: 'PAYMENT_APPROVAL', label: 'Payments' },
  { key: 'TASK', label: 'Tasks' },
  { key: 'FACILITY_MATURITY', label: 'Facilities' },
  { key: 'INVOICE_OVERDUE', label: 'Invoices' },
  { key: 'DSAR', label: 'DSARs' },
  { key: 'DORMANCY', label: 'Dormancy' },
];

const CATEGORY_COLORS = {
  COMPLAINT: 'red',
  ALERT: 'red',
  DSAR: 'purple',
  PAYMENT_APPROVAL: 'blue',
  KYC_REVIEW: 'amber',
  FACILITY_MATURITY: 'amber',
  INVOICE_OVERDUE: 'amber',
  TASK: 'blue',
  DORMANCY: 'gray',
};

const CATEGORY_LABELS = {
  COMPLAINT: 'Complaint',
  ALERT: 'Alert',
  DSAR: 'DSAR',
  PAYMENT_APPROVAL: 'Payment',
  KYC_REVIEW: 'KYC Review',
  FACILITY_MATURITY: 'Facility',
  INVOICE_OVERDUE: 'Invoice',
  TASK: 'Task',
  DORMANCY: 'Dormancy',
};

// Maps category → module key and optional navigation hints
function getNavigationTarget(item) {
  switch (item.category) {
    case 'ALERT': return { module: 'monitoring', label: 'View Alert' };
    case 'KYC_REVIEW': return { module: 'customers', label: 'Review KYC' };
    case 'COMPLAINT': return { module: 'crm', label: 'View Complaint' };
    case 'PAYMENT_APPROVAL': return { module: 'payments', label: 'Review Payment' };
    case 'FACILITY_MATURITY': return { module: 'lending', label: 'View Facility' };
    case 'INVOICE_OVERDUE': return { module: 'invoice-finance', label: 'View Invoice' };
    case 'TASK': return { module: 'crm', label: 'View Task' };
    case 'DSAR': return { module: 'compliance', label: 'View DSAR' };
    case 'DORMANCY': return { module: 'accounts', label: 'View Account' };
    default: return { module: 'crm', label: 'View' };
  }
}

export default function DashboardModule({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const pollRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (categoryFilter) params.set('category', categoryFilter);
      const result = await api.get(`/crm/dashboard?${params}`);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  // Initial load and category change
  useEffect(() => {
    setLoading(true);
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    pollRef.current = setInterval(fetchDashboard, 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchDashboard]);

  // Refresh on tab focus
  useEffect(() => {
    const onFocus = () => fetchDashboard();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchDashboard]);

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>
        Loading dashboard...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: T.red }}>
        Failed to load dashboard: {error}
      </div>
    );
  }

  const { summary = {}, actionItems = [] } = data || {};

  const toggleCategory = (key) => setCategoryFilter(prev => prev === key ? null : key);

  return (
    <div>
      {/* Title */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0, fontFamily: font }}>
          What Needs Attention Today
        </h2>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Alerts Open" value={summary.alertsOpen || 0} color={summary.alertsOpen > 0 ? T.red : T.text} onClick={() => toggleCategory('ALERT')} active={categoryFilter === 'ALERT'} />
        <StatCard label="Reviews Due" value={summary.reviewsDue || 0} color={summary.reviewsDue > 0 ? T.amber : T.text} onClick={() => toggleCategory('KYC_REVIEW')} active={categoryFilter === 'KYC_REVIEW'} />
        <StatCard label="Complaints" value={summary.complaintsActive || 0} color={summary.complaintsActive > 0 ? T.red : T.text} onClick={() => toggleCategory('COMPLAINT')} active={categoryFilter === 'COMPLAINT'} />
        <StatCard label="Payments Waiting" value={summary.paymentsWaiting || 0} onClick={() => toggleCategory('PAYMENT_APPROVAL')} active={categoryFilter === 'PAYMENT_APPROVAL'} />
        <StatCard label="Tasks Due" value={summary.tasksDue || 0} onClick={() => toggleCategory('TASK')} active={categoryFilter === 'TASK'} />
        <StatCard label="My Customers" value={summary.totalCustomers || 0} onClick={() => onNavigate && onNavigate('customers')} />
      </div>

      {/* Category filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.key || 'all'}
            onClick={() => setCategoryFilter(cat.key)}
            style={{
              padding: '5px 14px',
              borderRadius: 16,
              border: `1px solid ${categoryFilter === cat.key ? T.blue : T.border}`,
              background: categoryFilter === cat.key ? T.blueBg : T.surface,
              color: categoryFilter === cat.key ? T.blue : T.textSecondary,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: font,
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Action Queue */}
      {actionItems.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>&#10003;</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>All clear</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>No items need your attention right now.</div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {actionItems.map(item => (
            <ActionItem key={`${item.category}-${item.id}`} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionItem({ item, onNavigate }) {
  const nav = getNavigationTarget(item);
  const badgeColor = CATEGORY_COLORS[item.category] || 'gray';
  const badgeLabel = CATEGORY_LABELS[item.category] || item.category;

  const dueColor = item.daysRemaining === null
    ? T.textMuted
    : item.daysRemaining < 0 ? T.red
    : item.daysRemaining < 3 ? T.red
    : item.daysRemaining < 7 ? T.amber
    : T.textMuted;

  const priorityColor = item.priority >= 80 ? T.red
    : item.priority >= 60 ? T.amber
    : item.priority >= 40 ? T.blue
    : T.textMuted;

  return (
    <Card style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
      {/* Priority indicator */}
      <div style={{
        width: 4, alignSelf: 'stretch', borderRadius: 2,
        background: priorityColor, flexShrink: 0,
      }} />

      {/* Category badge */}
      <div style={{ flexShrink: 0, width: 90 }}>
        <Badge color={badgeColor}>{badgeLabel}</Badge>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          {item.customerName && (
            <span
              style={{ fontSize: 12, fontWeight: 600, color: T.text }}
            >
              {item.customerName}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 2 }}>
          {item.title}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted }}>
          {item.subtitle}
        </div>
      </div>

      {/* Due date / countdown */}
      {item.dueDate && (
        <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 80 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: dueColor }}>
            {item.daysRemaining < 0
              ? `${Math.abs(item.daysRemaining)}d overdue`
              : item.daysRemaining === 0 ? 'Today'
              : `${item.daysRemaining}d left`}
          </div>
          <div style={{ fontSize: 10, color: T.textMuted }}>
            {new Date(item.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </div>
        </div>
      )}

      {/* Action button */}
      <button
        onClick={() => onNavigate && onNavigate(nav.module, { entityType: item.entityType, entityId: item.entityId, customerId: item.customerId, category: item.category })}
        style={{
          flexShrink: 0, padding: '5px 12px', borderRadius: 5,
          border: `1px solid ${T.border}`, background: T.surface,
          color: T.blue, fontSize: 11, fontWeight: 500,
          cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap',
        }}
      >
        {nav.label}
      </button>
    </Card>
  );
}
